// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * React Query hooks for pipeline operations.
 *
 * Provides cached, deduplicated data fetching with automatic refetching
 * and optimistic updates for pipeline CRUD operations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getApiClient } from '@/app-shell/services'
import { workspaceConfigKeys } from '@/app-shell/hooks/useWorkspaceConfig'
import {
  createPipeline,
  updatePipeline,
  clonePipeline,
  deletePipeline,
  listPipelines,
  getAgentPipeline,
  saveAgentPipeline,
  pipelineKeys,
  type AgentPipelineUpsertRequest,
  type Pipeline,
  type PipelineCreateRequest,
  type PipelineCloneRequest,
  type QueuedPipelineJob,
} from '../services'
import type { WorkflowDefinition } from '../types'

/** Fetches the list of pipelines for a workspace */
export function usePipelinesList(organizationId?: string, workspaceId?: string) {
  return useQuery({
    queryKey: pipelineKeys.list(organizationId ?? '', workspaceId ?? ''),
    queryFn: () => listPipelines(getApiClient(), organizationId!, workspaceId!),
    enabled: Boolean(organizationId && workspaceId),
    throwOnError: false,
    placeholderData: undefined,
    staleTime: 5 * 60_000,
  })
}

/** Fetches an agent's pipeline (null when the agent has none yet) */
export function useAgentPipeline(organizationId?: string, workspaceId?: string, agentId?: string) {
  return useQuery({
    queryKey: pipelineKeys.agent(organizationId ?? '', workspaceId ?? '', agentId ?? ''),
    queryFn: () => getAgentPipeline(getApiClient(), organizationId!, workspaceId!, agentId!),
    enabled: Boolean(organizationId && workspaceId && agentId),
    throwOnError: false,
    staleTime: 5 * 60_000,
  })
}

interface UseSaveAgentPipelineOptions {
  organizationId?: string
  workspaceId?: string
  agentId?: string
  onSuccess?: (pipeline: Pipeline) => void
}

/** Creates or replaces an agent's pipeline (also points the agent at it) */
export function useSaveAgentPipeline({ organizationId, workspaceId, agentId, onSuccess }: UseSaveAgentPipelineOptions) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pipeline: AgentPipelineUpsertRequest) => {
      if (!organizationId || !workspaceId || !agentId) throw new Error('Missing context')
      return saveAgentPipeline(getApiClient(), organizationId, workspaceId, agentId, pipeline)
    },
    onSuccess: (pipeline) => {
      if (organizationId && workspaceId && agentId) {
        queryClient.invalidateQueries({
          queryKey: pipelineKeys.agent(organizationId, workspaceId, agentId),
        })
        // The save may re-point agent.pipelineId — refresh the agents list.
        queryClient.invalidateQueries({ queryKey: ['agents', organizationId, workspaceId] })
      }
      toast.success('Pipeline saved')
      onSuccess?.(pipeline)
    },
    onError: (error) => {
      console.error('Failed to save agent pipeline:', error)
      toast.error('Failed to save pipeline')
    },
  })
}

interface UseCreatePipelineOptions {
  organizationId?: string
  workspaceId?: string
  onSuccess?: (pipeline: Pipeline) => void
}

/** Creates a new pipeline */
export function useCreatePipeline({ organizationId, workspaceId, onSuccess }: UseCreatePipelineOptions) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { id?: string; name: string; description?: string; definition?: WorkflowDefinition }) => {
      if (!organizationId || !workspaceId) throw new Error('Missing context')

      const id = params.id || (globalThis.crypto?.randomUUID?.() ?? `pipeline-${Date.now()}`)
      const request: PipelineCreateRequest = {
        id,
        name: params.name,
        description: params.description,
        definition: params.definition ?? {
          nodes: [],
          edges: [],
          metadata: { name: params.name, version: '1.0' },
        },
      }
      return createPipeline(getApiClient(), organizationId, workspaceId, request)
    },
    onSuccess: (pipeline) => {
      if (organizationId && workspaceId) {
        queryClient.invalidateQueries({
          queryKey: pipelineKeys.list(organizationId, workspaceId),
        })
        queryClient.invalidateQueries({
          queryKey: workspaceConfigKeys.config(organizationId, workspaceId),
        })
      }
      toast.success('Created new pipeline')
      onSuccess?.(pipeline)
    },
    onError: (error) => {
      console.error('Failed to create pipeline:', error)
      toast.error('Failed to create pipeline')
    },
  })
}

interface UseUpdatePipelineOptions {
  organizationId?: string
  workspaceId?: string
  onSuccess?: (pipeline: Pipeline) => void
}

/** Updates a pipeline */
export function useUpdatePipeline({ organizationId, workspaceId, onSuccess }: UseUpdatePipelineOptions) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { pipelineId: string; updates: Partial<Pipeline> }) => {
      if (!organizationId || !workspaceId) throw new Error('Missing context')
      return updatePipeline(getApiClient(), organizationId, workspaceId, params.pipelineId, params.updates)
    },
    onSuccess: (pipeline) => {
      if (organizationId && workspaceId) {
        queryClient.invalidateQueries({
          queryKey: pipelineKeys.list(organizationId, workspaceId),
        })
        queryClient.invalidateQueries({
          queryKey: workspaceConfigKeys.config(organizationId, workspaceId),
        })
      }
      toast.success('Pipeline saved')
      onSuccess?.(pipeline)
    },
    onError: (error) => {
      console.error('Failed to save pipeline:', error)
      toast.error('Failed to save pipeline')
    },
  })
}

interface UseClonePipelineOptions {
  organizationId?: string
  workspaceId?: string
  onSuccess?: (pipeline: Pipeline) => void
}

/** Clones a pipeline */
export function useClonePipeline({ organizationId, workspaceId, onSuccess }: UseClonePipelineOptions) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params: { sourcePipelineId: string; newPipelineId: string; newName: string }) => {
      if (!organizationId || !workspaceId) throw new Error('Missing context')

      const request: PipelineCloneRequest = {
        new_pipeline_id: params.newPipelineId,
        new_name: params.newName,
      }
      return clonePipeline(getApiClient(), organizationId, workspaceId, params.sourcePipelineId, request)
    },
    retry: false,
    onSuccess: (pipeline) => {
      // Refresh workspace config so the new pipeline appears in the selector
      if (organizationId && workspaceId) {
        queryClient.invalidateQueries({
          queryKey: pipelineKeys.list(organizationId, workspaceId),
        })
        queryClient.invalidateQueries({
          queryKey: workspaceConfigKeys.config(organizationId, workspaceId),
        })
      }
      toast.success(`Cloned pipeline: ${pipeline.name}`)
      onSuccess?.(pipeline)
    },
    onError: (error) => {
      console.error('Failed to clone pipeline:', error)
      toast.error('Failed to clone pipeline')
    },
  })
}

interface UseDeletePipelineOptions {
  organizationId?: string
  workspaceId?: string
  onSuccess?: (deletedId: string) => void
}

/** Deletes a pipeline */
export function useDeletePipeline({ organizationId, workspaceId, onSuccess }: UseDeletePipelineOptions) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pipelineId: string) => {
      if (!organizationId || !workspaceId) throw new Error('Missing context')
      await deletePipeline(getApiClient(), organizationId, workspaceId, pipelineId)
      return pipelineId
    },
    onSuccess: (deletedId) => {
      if (organizationId && workspaceId) {
        queryClient.invalidateQueries({
          queryKey: pipelineKeys.list(organizationId, workspaceId),
        })
        queryClient.invalidateQueries({
          queryKey: workspaceConfigKeys.config(organizationId, workspaceId),
        })
      }
      toast.success('Pipeline deleted')
      onSuccess?.(deletedId)
    },
    onError: (error) => {
      console.error('Failed to delete pipeline:', error)
      toast.error('Failed to delete pipeline')
    },
  })
}

export { type QueuedPipelineJob }
